/**
 * WellCheckStorage — manages local storage of WellCheckDef content records.
 *
 * WellCheckDef content arrives as a ContentType='WellCheckDef' record from the backend.
 * The content_body column contains base64-encoded JSON that matches the C# WellCheckDefDto
 * structure (PascalCase on the wire).
 *
 * Storage structure:
 *   Key:   wellcheck_def_{languageTypeId}   e.g. wellcheck_def_1
 *   Value: JSON-serialised WellCheckDefDto (camelCase)
 *   Index: wellcheck_def_index              array of stored languageTypeIds
 *
 * Wire format (base64 → JSON, PascalCase):
 * {
 *   "LanguageTypeId": "1",
 *   "Vaccines": [ { "VaccineId": "...", "Name": "...", "DName": "..." } ],
 *   "VacSchedules": [
 *     {
 *       "ScheduleId": "...",
 *       "Name": "...",
 *       "Events": [ { "EventId": "...", "Name": "...", "Period": "...", "Days": "...", "VaccineId": "..." } ]
 *     }
 *   ],
 *   "Schedules": [
 *     {
 *       "ScheduleId": "...",
 *       "Name": "...",
 *       "VSName": "...",
 *       "VSId": "...",
 *       "Events": [
 *         { "EventId": "...", "Name": "...", "Days": "...",
 *           "Vaccines": [ { "EventId": "...", "Name": "...", "Period": "...", "Days": "...", "VaccineId": "..." } ] }
 *       ]
 *     }
 *   ]
 * }
 */

import type { ContentRecord } from '../stores/contentStore';

export interface StorageInterface {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

// ─── DTO interfaces (camelCase, mirrors C# PascalCase) ───────────────────────

/**
 * Represents the instantiated state of a single well-check visit event for a profile.
 * Stored as part of the profile's wellchecks array and synced to the backend.
 */
export interface WellCheckDto {
  /** Stable visit instance identifier for backend round-trip correlation */
  id?: string;
  /** Local-only toggle for Action Plan tracking (not part of backend DTO contract) */
  trackAction?: boolean;
  /** References WellCheckSchEvntDto.eventId from the selected schedule */
  eventId: string;
  /** Planned or completed visit date (ISO string or display date) */
  date: string;
  /** Visit type: 'In-Office', 'Online', or 'Self-Administered' */
  type?: string;
  /** Location of the well-child visit */
  location: string;
  /** Visit status (e.g. 'pending', 'completed', 'missed', 'timed_out', 'snapshot_completed', 'appt_completed') */
  status: string;
  /** Outcome / disposition notes entered after appointment completion */
  outcome?: string;
}

// ─── Vaccine definition DTOs (mirrors C# VaccineDto / VaccineScheduleDto) ───────────────

/** A single vaccine definition. Mirrors C# VaccineDto. */
export interface VaccineDto {
  vaccineId: string;
  name: string;
  dName: string;
}

/** A single event entry within a vaccine schedule. Mirrors C# VaccineSchEvntDto. */
export interface VaccineSchEvntDto {
  eventId: string;
  name: string;
  period: string;
  days: string;
  vaccineId: string;
}

/** A single vaccine schedule definition. Mirrors C# VaccineScheduleDto. */
export interface VaccineScheduleDto {
  scheduleId: string;
  name: string;
  events: VaccineSchEvntDto[];
}

export interface WellCheckSchEvntDto {
  /** Event identifier */
  eventId: string;
  /** Display name for this well-check event (e.g. "2 Month Visit") */
  name: string;
  /** Age/days value (e.g. "60") */
  days: string;
  /** Vaccine schedule events recommended at this visit */
  vaccines: VaccineSchEvntDto[];
}

export interface WellCheckScheduleDto {
  /** Schedule identifier */
  scheduleId: string;
  /** Display name (e.g. "Standard ACIP Schedule") */
  name: string;
  /** Short/VS name for compact display */
  vsName: string;
  /** Links to the VaccineScheduleDto.scheduleId used for this well-check schedule */
  vsId: string;
  /** Ordered list of well-check visit events */
  events: WellCheckSchEvntDto[];
}

/** Combined well-check definition record. Mirrors C# WellCheckDefDto. */
export interface WellCheckDefDto {
  languageTypeId: string;
  /** Flat list of all vaccine definitions */
  vaccines: VaccineDto[];
  /** Linked vaccine schedule definitions */
  vacSchedules: VaccineScheduleDto[];
  /** Well-check visit schedules */
  schedules: WellCheckScheduleDto[];
  /** ISO timestamp set when the record was stored locally */
  downloadedAt: string;
}

// ─── Growth & Vaccine tracking DTOs (mirrors C# PascalCase definitions) ───────

/** A single height/weight measurement at a point in time. Mirrors C# GrowthDataDto. */
export interface GrowthDataDto {
  /** Height value as a string (unit determined by user preference: in or cm) */
  height: string;
  /** Weight value as a string (unit determined by user preference: lb or kg) */
  weight: string;
  /** Date of measurement (ISO string) */
  date: string;
  /** Links to WellCheckSchEvntDto.eventId from the associated well-check visit */
  eventId?: string;
  /** Ounces component when English units and child is under 1 month old */
  weightOz?: string;
}

/** Container for all growth measurements for a profile. Mirrors C# GrowthTrackDto. */
export interface GrowthTrackDto {
  data: GrowthDataDto[];
}

/** A single vaccine administration record. Mirrors C# VaccineEventDto. */
export interface VaccineEventDto {
  /** Vaccine name (e.g. 'DTaP', 'MMR') */
  name: string;
  /** Date vaccine was administered (ISO string) */
  date: string;
  /** Links to VaccineSchEvntDto.eventId in the selected VaccineScheduleDto */
  eventId?: string;
}

/** Container for all vaccine administration records for a profile. Mirrors C# VaccineTrackDto. */
export interface VaccineTrackDto {
  events: VaccineEventDto[];
}

/** A single prescription record. Mirrors C# PrescriptionEventDto. */
export interface PrescriptionEventDto {
  id?: string;
  name: string;
  date: string;
  freq: string;
  qty?: string;
  totalDoses?: string;  // Local UI alias for Qty (e.g. "30 tablets") — used to estimate when supply runs out
  refills: string;
  Track?: string;
  track?: string;
  status: string;     // 'Active' | 'Refill Needed' | 'On Hold' | 'Completed'
  trackAction?: boolean;
}

/** Container for all prescription records for a profile. Mirrors C# PrescriptionTrackDto. */
export interface PrescriptionTrackDto {
  events: PrescriptionEventDto[];
}

/** A single referral record. Mirrors C# ReferralEventDto. */
export interface ReferralEventDto {
  id?: string;
  to: string;
  for: string;
  date: string;
  contactInfo: string;
  outcome: string;
  Track?: string;
  track?: string;
  status: string;
  trackAction?: boolean;
}

/** Container for all referral records for a profile. Mirrors C# ReferralTrackDto. */
export interface ReferralTrackDto {
  events: ReferralEventDto[];
}

// ─── Storage class ────────────────────────────────────────────────────────────

export class WellCheckStorage {
  private readonly keyPrefix = 'wellcheck_def_';
  private readonly indexKey  = 'wellcheck_def_index';

  constructor(private storage: StorageInterface) {}

  // ─── Private helpers ────────────────────────────────────────────────────────

  private storageKey(languageTypeId: string): string {
    return `${this.keyPrefix}${languageTypeId}`;
  }

  private decodeBase64(b64: string): string {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const buf: any = (globalThis as any).Buffer;
      if (buf) {
        return buf.from(b64, 'base64').toString('utf-8');
      }
      return atob(b64);
    } catch (err) {
      throw new Error(`WellCheckStorage: failed to decode base64 — ${err}`);
    }
  }

  private async addToIndex(languageTypeId: string): Promise<void> {
    await this.addToNamedIndex(this.indexKey, languageTypeId);
  }

  private async removeFromIndex(languageTypeId: string): Promise<void> {
    await this.removeFromNamedIndex(this.indexKey, languageTypeId);
  }

  private async getIndex(): Promise<string[]> {
    return this.getNamedIndex(this.indexKey);
  }

  private async addToNamedIndex(indexKey: string, languageTypeId: string): Promise<void> {
    try {
      const raw = await this.storage.getItem(indexKey);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      if (!ids.includes(languageTypeId)) {
        ids.push(languageTypeId);
        await this.storage.setItem(indexKey, JSON.stringify(ids));
      }
    } catch (_) { /* non-fatal */ }
  }

  private async removeFromNamedIndex(indexKey: string, languageTypeId: string): Promise<void> {
    try {
      const raw = await this.storage.getItem(indexKey);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      const updated = ids.filter(id => id !== languageTypeId);
      await this.storage.setItem(indexKey, JSON.stringify(updated));
    } catch (_) { /* non-fatal */ }
  }

  private async getNamedIndex(indexKey: string): Promise<string[]> {
    try {
      const raw = await this.storage.getItem(indexKey);
      return raw ? JSON.parse(raw) : [];
    } catch (_) {
      return [];
    }
  }


  // ─── Public API ──────────────────────────────────────────────────────────────

  /**
   * Persist a WellCheckDefDto keyed by languageTypeId.
   */
  async saveWellCheckDef(dto: WellCheckDefDto): Promise<boolean> {
    try {
      const record: WellCheckDefDto = { ...dto, downloadedAt: dto.downloadedAt || new Date().toISOString() };
      const langId = record.languageTypeId || 'unknown';
      await this.storage.setItem(this.storageKey(langId), JSON.stringify(record));
      await this.addToIndex(langId);
      console.log(`✅ WellCheckStorage: saved WellCheckDef (${record.schedules.length} schedule(s), ${record.vaccines.length} vaccine(s), ${record.vacSchedules.length} vacSchedule(s)) for languageTypeId="${langId}"`);
      return true;
    } catch (err) {
      console.error('❌ WellCheckStorage.saveWellCheckDef failed:', err);
      return false;
    }
  }

  /**
   * Retrieve a stored WellCheckDefDto by languageTypeId.
   * Falls back to the first available record when languageTypeId is omitted.
   */
  async getWellCheckDef(languageTypeId?: string): Promise<WellCheckDefDto | null> {
    try {
      if (languageTypeId) {
        const raw = await this.storage.getItem(this.storageKey(languageTypeId));
        return raw ? (JSON.parse(raw) as WellCheckDefDto) : null;
      }
      // Fallback: return first available
      const ids = await this.getIndex();
      if (ids.length === 0) return null;
      const raw = await this.storage.getItem(this.storageKey(ids[0]));
      return raw ? (JSON.parse(raw) as WellCheckDefDto) : null;
    } catch (err) {
      console.error('❌ WellCheckStorage.getWellCheckDef failed:', languageTypeId, err);
      return null;
    }
  }

  /**
   * Return all stored WellCheckDefDto records.
   */
  async getAllWellCheckDefs(): Promise<WellCheckDefDto[]> {
    const ids = await this.getIndex();
    const results: WellCheckDefDto[] = [];
    for (const id of ids) {
      const dto = await this.getWellCheckDef(id);
      if (dto) results.push(dto);
    }
    return results;
  }

  /**
   * Delete a WellCheckDefDto by languageTypeId.
   */
  async deleteWellCheckDef(languageTypeId: string): Promise<boolean> {
    try {
      await this.storage.removeItem(this.storageKey(languageTypeId));
      await this.removeFromIndex(languageTypeId);
      console.log(`🗑️ WellCheckStorage: deleted languageTypeId="${languageTypeId}"`);
      return true;
    } catch (err) {
      console.error('❌ WellCheckStorage.deleteWellCheckDef failed:', languageTypeId, err);
      return false;
    }
  }

  /**
   * Remove all stored WellCheckDef records and the index.
   */
  async clearAll(): Promise<void> {
    const ids = await this.getIndex();
    for (const id of ids) {
      try { await this.storage.removeItem(this.storageKey(id)); } catch (_) { /* continue */ }
    }
    try { await this.storage.removeItem(this.indexKey); } catch (_) { /* non-fatal */ }
    console.log('🗑️ WellCheckStorage: cleared all records');
  }

  // ─── Content-record unpacking ──────────────────────────────────────────────

  /**
   * Unpack a ContentStore record of type "WellCheckDef" and persist it.
   *
   * Expected record.tableData shape (standard content-server response):
   *   Columns: [..., "content_body", ...]
   *   Rows:    [[..., "<base64-encoded JSON>", ...]]
   *
   * The base64-encoded JSON payload must match the C# WellCheckDefDto structure.
   * Returns the stored dto on success, null on failure.
   */
  async unpackFromContentRecord(record: ContentRecord): Promise<WellCheckDefDto | null> {
    try {
      console.log('\n🔍 WellCheckStorage.unpackFromContentRecord called for:', record.id);

      const tableData = (record as any).tableData;
      if (!tableData?.Data?.Columns || !tableData?.Data?.Rows?.length) {
        console.warn('⚠️ WellCheckStorage: ContentRecord has no tableData rows');
        return null;
      }

      const columns: string[] = tableData.Data.Columns;
      const bodyIdx = columns.findIndex((c: string) => c.toLowerCase() === 'content_body');
      if (bodyIdx === -1) {
        console.warn('⚠️ WellCheckStorage: content_body column not found. Available:', columns);
        return null;
      }

      const firstRow = tableData.Data.Rows[0];
      const encodedBody: string = firstRow[bodyIdx];
      if (!encodedBody) {
        console.warn('⚠️ WellCheckStorage: content_body is empty');
        return null;
      }

      const p = JSON.parse(this.decodeBase64(encodedBody));

      // ── Map PascalCase → camelCase ──────────────────────────────────────────

      const vaccines: VaccineDto[] = (p.Vaccines ?? p.vaccines ?? []).map((v: any): VaccineDto => ({
        vaccineId: v.VaccineId ?? v.vaccineId ?? '',
        name:      v.Name      ?? v.name      ?? '',
        dName:     v.DName     ?? v.dName     ?? '',
      }));

      const vacSchedules: VaccineScheduleDto[] = (p.VacSchedules ?? p.vacSchedules ?? []).map((s: any): VaccineScheduleDto => ({
        scheduleId: s.ScheduleId ?? s.scheduleId ?? '',
        name:       s.Name       ?? s.name       ?? '',
        events: (s.Events ?? s.events ?? []).map((e: any): VaccineSchEvntDto => ({
          eventId:   e.EventId   ?? e.eventId   ?? '',
          name:      e.Name      ?? e.name      ?? '',
          period:    e.Period    ?? e.period    ?? '',
          days:      e.Days      ?? e.days      ?? '',
          vaccineId: e.VaccineId ?? e.vaccineId ?? '',
        })),
      }));

      const schedules: WellCheckScheduleDto[] = (p.Schedules ?? p.schedules ?? []).map((s: any): WellCheckScheduleDto => ({
        scheduleId: s.ScheduleId ?? s.scheduleId ?? '',
        name:       s.Name       ?? s.name       ?? '',
        vsName:     s.VSName     ?? s.vsName      ?? '',
        vsId:       s.VSId       ?? s.vsId        ?? '',
        events: (s.Events ?? s.events ?? []).map((e: any): WellCheckSchEvntDto => ({
          eventId:  e.EventId  ?? e.eventId  ?? '',
          name:     e.Name     ?? e.name     ?? '',
          days:     e.Days     ?? e.days     ?? '',
          vaccines: (e.Vaccines ?? e.vaccines ?? []).map((v: any): VaccineSchEvntDto => ({
            eventId:   v.EventId   ?? v.eventId   ?? '',
            name:      v.Name      ?? v.name      ?? '',
            period:    v.Period    ?? v.period    ?? '',
            days:      v.Days      ?? v.days      ?? '',
            vaccineId: v.VaccineId ?? v.vaccineId ?? '',
          })),
        })),
      }));

      const dto: WellCheckDefDto = {
        languageTypeId: p.LanguageTypeId ?? p.languageTypeId ?? '1',
        vaccines,
        vacSchedules,
        schedules,
        downloadedAt: new Date().toISOString(),
      };

      console.log(`📋 WellCheckStorage: parsed WellCheckDef — ${dto.schedules.length} schedule(s), ${dto.vaccines.length} vaccine(s), ${dto.vacSchedules.length} vacSchedule(s) for languageTypeId="${dto.languageTypeId}"`);

      const saved = await this.saveWellCheckDef(dto);
      if (!saved) return null;

      console.log(`✅ WellCheckStorage: unpacked & stored WellCheckDef (lang="${dto.languageTypeId}")`);
      return dto;
    } catch (err) {
      console.error('❌ WellCheckStorage.unpackFromContentRecord failed:', err);
      return null;
    }
  }
}
